<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：アラートリスト
 *
 * 作成者：2022/02/28 ノア）虞
 * 最終修正者：2022/02/28 ノア）虞
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.alertList.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- データ -->
    <section class="tti-padding-content">
      <div class="tti-content-title alertListSearchInfo">
        <el-form class="tti-form" :inline="true" ref="form" :model="alertListSearchInfo" size="mini" :rules="rules">
          <el-form-item class="tti-form-item" prop="startMonth" :label="$t('master.alertList.operationStartDay')">
            <el-date-picker
              v-model="alertListSearchInfo.startMonth"
              type="month"
              size="small"
              format="yyyy/MM"
              value-format="yyyy/MM"
              :picker-options="pickerOptions"
              :editable="false"
              :clearable="false"
              v-tooltip="getTooltip(tooltipType.startMonthTip)"
            ></el-date-picker>
          </el-form-item>
          <el-form-item class="tti-form-item" prop="endMonth" :label="$t('master.alertList.operationEndDay')">
            <el-date-picker
              v-model="alertListSearchInfo.endMonth"
              type="month"
              size="small"
              format="yyyy/MM"
              value-format="yyyy/MM"
              :picker-options="pickerOptions"
              :editable="false"
              :clearable="false"
              v-tooltip="getTooltip(tooltipType.endMonthTip)"
            ></el-date-picker>
          </el-form-item>
          <el-form-item class="tti-form-item" prop="selectType" :label="$t('master.alertList.selectType')">
            <el-radio-group v-model="alertListSearchInfo.selectType" @change="onChangeSelectType">
              <el-radio v-for="item in selectTypeList" :key="item.code" :label="item.code">
                {{ item.name }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            class="tti-form-item"
            prop="continuationMonth"
            :label="$t('master.alertList.continuationMonth')"
            v-if="alertListSearchInfo.selectType === SelectType.overFlag"
          >
            <el-input class="tti-input" v-model.trim="alertListSearchInfo.continuationMonth" size="small" align="right">
            </el-input>
            <span class="tti-unit">
              {{ $t('master.alertList.monthLabel') }}
            </span>
          </el-form-item>
          <el-form-item>
            <el-button
              class="tti-btn-jump tti-btn-default tti-btn-001"
              v-tooltip="getTooltip(tooltipType.alertSearchTip)"
              @click="onDoSearch"
            >
              <tti-svg-icon icon-name="su_016"> </tti-svg-icon>{{ $t('buttons.search') }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
      <el-table
        :data="alertListData"
        :empty-text="$t('messages.common.emptyTableData')"
        height="calc(100vh - 210px)"
        border
        header-row-class-name="tti-master-table-header"
      >
        <!-- № -->
        <el-table-column :label="$t('master.alertList.number')" width="70" :sortable="false" align="center">
          <template slot-scope="scope">
            <span>{{ indexMethod(scope.row, scope.$index) }}</span>
          </template>
        </el-table-column>
        <!-- 営業所名 -->
        <el-table-column
          prop="businessOfficeName"
          :label="$t('master.alertList.businessOfficeName')"
          width="218"
          :sortable="true"
        ></el-table-column>
        <!-- 利用開始日 -->
        <el-table-column
          prop="startUseDate"
          :label="$t('master.alertList.startUseDate')"
          width="120"
          :sortable="true"
          align="left"
        ></el-table-column>
        <!-- SigfoxID -->
        <el-table-column
          prop="sigfoxId"
          :label="$t('master.alertList.sigfoxId')"
          width="200"
          :sortable="true"
        ></el-table-column>
        <!-- 管理対象名称 -->
        <el-table-column
          prop="manageTargetName"
          :label="$t('master.alertList.manageTargetName', [managementTargetShowName])"
          width="218"
          :sortable="true"
        ></el-table-column>
        <!-- 期間指定の月別受信回数 -->
        <template v-for="(operating, index) in alertListData[0] ? alertListData[0].countList : []">
          <el-table-column width="100" :key="index" :label="operating.month" align="right" :sortable="true">
            <template slot-scope="scope">
              <div :class="getDeviceStatusBgColor(scope.row.countList[index].overFlag)" size="small">
                {{ scope.row.countList[index].count }}
              </div>
            </template>
          </el-table-column>
        </template>
        <!-- 月別受信回数合計 -->
        <el-table-column
          prop="total"
          :label="$t('master.alertList.total')"
          width="100"
          align="right"
          :sortable="true"
        ></el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref, Vue, Watch } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { differenceInMonths } from 'date-fns'
import { toNumber } from 'lodash'

import { i18n } from '@/locales'
import { getAlertListData } from '@/store/apis'
import { AlertListSearchInfo, General, AlertListSearchItem } from '@/models'
import { SelectType } from '@/commons/constants'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { RULES, validateService } from '@/service/validateService'
import { isDateAfter, parseDate, pickerOptions } from '@/utils/dateUtils'
import { localService } from '@/commons/services/localService'
import { common } from '@/store/modules'

/**
 * 対象種類（"0" : すべて、"1"：受信回数を超えるのみ）
 */
const SelectTypeList = [
  { code: '0', name: 'すべて' },
  { code: '1', name: '受信回数を超えるのみ(10回/日×月日数)' }
]

/**
 * アラートリスト
 *
 * @class
 */
@Component({
  name: 'alert-list'
})
export default class ALERT_LIST extends Vue {
  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptions

  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  SelectType = SelectType

  /**
   * 気に入り営業所
   */
  businessOfficeIdList: string[] = []

  /**
   * 検索条件
   */
  alertListSearchInfo: AlertListSearchInfo = {
    /**
     * 開始月
     */
    startMonth: '',

    /**
     * 終了月
     */
    endMonth: '',

    /**
     * 対象種類（"0" : すべて、"1"：受信回数を超えるのみ）
     */
    selectType: SelectType.all,

    /**
     * 連続月(1~12)
     */
    continuationMonth: ''
  }

  /**
   * 対象種類
   */
  selectTypeList: General[] = SelectTypeList

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {
    /**
     * 開始月
     */
    startMonth: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.alertList.startMonth')]).toString(),
        trigger: 'blur'
      }
    ],
    /**
     * 終了月
     */
    endMonth: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.alertList.endMonth')]).toString(),
        trigger: 'blur'
      }
    ],
    /**
     * 連続月(1~12)
     */
    continuationMonth: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.alertList.continuationMonthLabel')]).toString(),
        trigger: 'blur'
      },
      {
        validator: validateService.getValidator([
          {
            rule: RULES.NUMBERRANGE,
            range: [1, 12],
            message: i18n
              .t('vaildate.common.numberRange', [i18n.t('master.alertList.continuationMonthLabel').toString(), 1, 12])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ]
  }

  /**
   * アラート
   */
  alertListData: AlertListSearchItem[] = []

  /**
   * マスタ画面ツールチップタイプ
   */
  tooltipType = {
    // 戻る
    back: 'back',
    // アラートリストを検索する
    alertSearchTip: 'alertSearchTip',
    // 開始月
    startMonthTip: 'startMonthTip',
    // 終了月
    endMonthTip: 'endMonthTip'
  }

  /**
   * 管理対象表示名
   *
   * @returns {string} 管理対象表示名
   */
  get managementTargetShowName(): string {
    return localService.userInfo?.managementTargetShowName
  }

  /**
   * 気に入り営業所
   *
   * @returns {string[]} 気に入り営業所
   */
  get watchedFavOfficeList(): string[] {
    return common.favOfficeList
  }

  /**
   * 気に入り営業所変更した時の処理
   *
   * @param {string[]} value  気に入り営業所
   * @returns {void}
   */
  @Watch('watchedFavOfficeList', { immediate: true, deep: true })
  onFavOfficeChange(value: string[]): void {
    if (!value || value.length === 0) {
      return
    }
    this.businessOfficeIdList = [...value]
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('MST_GPS_TRK_SET > mounted')
  }

  /**
   * 検索ボタンを押した後の処理
   *
   * @returns {void}
   */
  onDoSearch(): void {
    this.formRef.validate((valid: boolean) => {
      if (!valid) return false

      if (!this.correlationCheck()) return
      this.fetchAlertListData()
    })
  }

  /**
   * 受信回数アラートリスト
   */
  async fetchAlertListData(): Promise<void> {
    let params = {
      businessOfficeIdList: this.businessOfficeIdList,
      startMonth: this.alertListSearchInfo.startMonth,
      endMonth: this.alertListSearchInfo.endMonth,
      selectType: this.alertListSearchInfo.selectType
    } as AlertListSearchInfo

    if (this.alertListSearchInfo.selectType === SelectType.overFlag) {
      params.continuationMonth = this.alertListSearchInfo.continuationMonth
    }

    const alertListData = await getAlertListData(params)
    this.alertListData = (alertListData.list as AlertListSearchItem[]) || []
  }

  /**
   * 相関チェックを行う
   *
   * @returns {boolean} 相関チェック結果
   */
  correlationCheck(): boolean {
    if (isDateAfter(this.alertListSearchInfo.startMonth, this.alertListSearchInfo.endMonth, 'yyyy/MM')) {
      msgBoxService.info(
        i18n
          .t('vaildate.common.dateRange', [i18n.t('master.alertList.startMonth'), i18n.t('master.alertList.endMonth')])
          .toString()
      )
      return false
    }

    // 開始月から終了月までの選択された期間は30月を超える場合
    const diff = differenceInMonths(
      parseDate(this.alertListSearchInfo.endMonth + '/01'),
      parseDate(this.alertListSearchInfo.startMonth + '/01')
    )

    if (diff > 30) {
      msgBoxService.info(i18n.t('vaildate.common.differenceInMonths', [30]).toString())
      return false
    }

    if (this.alertListSearchInfo.continuationMonth) {
      const continuationMonth = toNumber(this.alertListSearchInfo.continuationMonth)
      if (!isNaN(continuationMonth) && diff + 1 < continuationMonth) {
        msgBoxService.info(
          i18n
            .t('vaildate.common.dateRange', [
              i18n.t('master.alertList.operation'),
              i18n.t('master.alertList.continuationMonthLabel')
            ])
            .toString()
        )
        return false
      }
    }

    return true
  }

  /**
   * 画面ツールチップを取得する
   *
   * @param {string} key ツールチップキー
   * @returns {any} ツールチップ
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTooltip(key: string): any {
    return {
      content: i18n.t(`master.tooltipCommon.${key}`),
      placement: 'bottom-start',
      delay: { show: 1000, hide: 0 }
    }
  }

  /**
   * 対象種類変化する
   *
   * @returns {void}
   */
  onChangeSelectType(): void {
    this.alertListSearchInfo.continuationMonth = ''
  }

  /**
   * 前画面へ戻る
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    window.history.back()
  }

  /**
   * カスタムインデックス
   *
   * @param {AlertListSearchItem} row アラート
   * @param {number} index インデックス
   * @returns {number} ンデックス
   */
  indexMethod(row: AlertListSearchItem, index: number): number {
    row.number = index + 1
    return index + 1
  }

  /**
   * 稼働状況背景色を取得する
   *
   * @param {boolean} overFlag 超える場合
   * @returns {string} 表示名
   */
  getDeviceStatusBgColor(overFlag: boolean): BackgroundColor {
    // 超える場合
    if (overFlag) {
      return { redBackgroundColor: true }
    }

    return { redBackgroundColor: false }
  }
}

/**
 * 背景色
 *
 * @interface
 */
interface BackgroundColor {
  /**
   * 赤
   */
  redBackgroundColor?: boolean
}
</script>

<style lang="scss" scoped>
::v-deep .redBackgroundColor {
  background-color: $C23;
}

.tti-content-title.alertListSearchInfo {
  border-bottom: $C05 solid 1px;
  padding-bottom: 6px;
}

.content {
  overflow: auto;
  height: calc(100% - (226px + 12px * 2 + (39px + 6px + 10px) - 170px));
}

.tti-selection {
  width: 80px;
}

.tti-input {
  width: 80px;
}

.tti-form-child {
  margin: 12px 0;
}

.tti-form-child-level-1,
.tti-form-child-level-2 {
  margin-left: 32px;
}

.tti-form-block {
  border-bottom: $C05 solid 1px;
  margin-bottom: 10px;
}

.tti-form-block:nth-last-child(1) {
  border-bottom: none;
  margin-bottom: 0;
}

.tti-form-item {
  margin-right: 12px;
}

.tti-unit {
  margin-left: 6px;
}

::v-deep .el-form-item__label {
  padding-right: 24px;
}
</style>
